import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AddressAdapter } from '../partial/address.model';
import { ContactInfo, ContactInfoAdapter } from '../partial/contact-info.model';
import { HumanName, HumanNameAdapter } from '../partial/human-name.model';
import { Address } from '@xt/domain-model';
import { Store } from '@ngrx/store';
import { selectMessagingRootId } from '@core/store/selectors/app-config.selectors';

export interface Human {
  name: string;
  acronym: string;
  nameDetails?: HumanName;
  address: string;
  addressDetails?: Address;
  email?: string;
  contactDetails?: ContactInfo;
  birthdate?: Date;
  gender?: fhir4.Practitioner['gender'] | fhir4.Patient['gender'];
  firstName?: string;
  lastName?: string;
  identifier: {};
  userIdentifiers?: string[];
  userIdentifier?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BasePersonAdapter {
  constructor(
    private nameAdapter: HumanNameAdapter,
    private addressAdapter: AddressAdapter,
    private contactInfoAdapter: ContactInfoAdapter,
    private translocoService: TranslocoService,
    protected store: Store,
  ) {}

  fromResource$(
    fhirHuman: fhir4.Patient | fhir4.Practitioner | fhir4.Person,
  ): Observable<Human> {
    return this.store.pipe(
      selectMessagingRootId,
      switchMap((messagingRootId) =>
        forkJoin([
          this.nameAdapter.fromResource$(fhirHuman.name),
          this.addressAdapter.fromResource$(fhirHuman.address),
          this.contactInfoAdapter.fromResource$(fhirHuman.telecom),
        ]).pipe(
          map(([nameDetails, addressDetails, contactDetails]) => {
            const name = nameDetails.display;
            const { acronym } = nameDetails;
            const address = addressDetails.display;
            const { email } = contactDetails;

            const birthdate = fhirHuman.birthDate
              ? new Date(fhirHuman.birthDate)
              : undefined;
            const gender = fhirHuman.gender;
            const matrixId =
              fhirHuman.identifier.filter(
                (identifier) => identifier.type?.coding[0]?.code === 'MatrixId',
              )[0]?.value ||
              fhirHuman.identifier.filter(
                (identifier) =>
                  identifier?.type?.coding[0]?.code === 'MATRIX_ID',
              )[0]?.value ||
              fhirHuman.identifier.filter(
                (identifier) => identifier?.system?.includes(messagingRootId),
              )[0]?.value ||
              null;

            const mrIdentifier = fhirHuman.identifier.filter(
              (identifier) => identifier.type?.coding[0].code === 'MR',
            );

            const userIdentifiers = mrIdentifier.map(
              (identifier) => identifier.value,
            );

            return {
              name,
              acronym,
              nameDetails,
              address,
              addressDetails,
              email,
              contactDetails,
              birthdate,
              gender,
              firstName: nameDetails.firstName,
              lastName: nameDetails.lastName,
              userIdentifier: userIdentifiers.length ? userIdentifiers[0] : '',
              userIdentifiers,
              identifier: {
                system: fhirHuman.identifier[0].system,
                value: fhirHuman.identifier[0].value,
              },
              matrixId,
            };
          }),
        ),
      ),
    );
  }

  getHumanGenderTranslated(human: Human): string {
    const gender = human?.gender || 'unknown';
    const genderTranslationKey = `values.gender.${gender}`;
    const genderTranslationKeyFallback = `values.gender.unknown`;

    const value = this.translocoService.translate(genderTranslationKey);
    const valueFallback = this.translocoService.translate(
      genderTranslationKeyFallback,
    );

    return value !== genderTranslationKey ? value : valueFallback;
  }
}
